import { useLazyQuery } from '@apollo/client';
import { useDealPromptFeatureFlaggedFilters } from '@openx/components-deal-prompt/lib/hooks/useDealPromptFeatureFlaggedFilters';
import { buildSearchQueryInput } from '@openx/components-deal-prompt/lib/utils/buildSearchQueryInput';
import type {
  DealPromptSearchFetchArgs,
  UseDealPromptAppsSearchFetch,
} from '@openx/components-deal-prompt/lib/utils/context/type';
import { mapCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers';
import { type DealPromptFilters, dealPromptContextTypes } from '@openx/components-deal-prompt/lib/utils/types';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { useCallback } from 'react';
import { InventoryBundleSearchDocument } from '../../../graphql/query/dealPrompt/GetInventoryBundleSearch.generated';

export const useDealPromptAppsSearchFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptAppsSearchFetch => {
  const [fetch] = useLazyQuery(InventoryBundleSearchDocument, {
    // TODO: EXCH-14467 remove this caching once duplicte id change is merged
    fetchPolicy: 'no-cache',
  });
  const featureFlaggedFilters = useDealPromptFeatureFlaggedFilters();

  const fetchDealPromptApps = useCallback(
    async ({ query, queries }: DealPromptSearchFetchArgs, abortSignal?: AbortSignal) => {
      const input = {
        query: buildSearchQueryInput({ query, queries }),
        sortBy: 'totalImpressions',
        view: 'desc',
        excludeQuery: [],
        filters: mapCriteriaToFiltersBody(criteria, dealPromptContextTypes.APPS, featureFlaggedFilters),
      };

      const result = await fetch({
        variables: {
          input,
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      return {
        cumulativeData: result.data?.inventoryBundleSearch.body.cumulativeData ?? [],
        numberOfResults: result?.data?.inventoryBundleSearch.body.totalRecords ?? 0,
        _error: result?.error,
      };
    },
    [fetch, criteria, featureFlaggedFilters]
  );

  return {
    fetchDealPromptApps,
  };
};
